import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import * as XLSX from "xlsx";
import figLogo from "../../assets/fig_CROP.png";
import MediaViewer from "../../components/mediaviewer";

// API endpoint (using the FINANCE_expenses table)
const API_EXPENSES = "https://figazuresql.swancreate.com/api/swa/FINANCE_expenses";

// Hardcoded style/colors
const textColor = "#000";
const backgroundColor = "#fff";
const headerBackground = "#f5f5f5";
const alternateRowEven = "#fff";
const alternateRowOdd = "#f9f9f9";
const accentColor = "#4cceac"; // for buttons

// Helper to format currency
const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    }).format(value);

const FederalReimbursementReport = () => {
    // Report filters: Month and Program (not Vendor)
    const [selectedMonth, setSelectedMonth] = useState("All");
    const [selectedProgram, setSelectedProgram] = useState("All");
    const [expenseData, setExpenseData] = useState([]);
    // State for tracking collapsed vendor groups
    const [collapsedGroups, setCollapsedGroups] = useState({});
    // MediaViewer state
    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [mediaImageUrl, setMediaImageUrl] = useState("");

    // Fetch expenses from the API and include only those with a valid reimbursement (non-null ReimbursementTrackingId)
    useEffect(() => {
        fetchExpenses();
    }, [selectedMonth, selectedProgram]);

    const fetchExpenses = async () => {
        try {
            const response = await axios.get(API_EXPENSES);
            const expenses = response.data
                .map((expense) => ({
                    ...expense,
                    TotalAmount: parseFloat(expense.Amount) || 0,
                    ExpenseDate: expense.ExpenseDate ? new Date(expense.ExpenseDate) : null,
                }))
                .filter((expense) => expense.ReimbursementTrackingId !== null);
            // Apply month filter
            const monthFiltered =
                selectedMonth === "All"
                    ? expenses
                    : expenses.filter(
                        (expense) =>
                            expense.ExpenseDate &&
                            expense.ExpenseDate.getMonth() + 1 === parseInt(selectedMonth, 10)
                    );
            // Apply program filter based on ReimbursmentTrackingProgram
            const programFiltered =
                selectedProgram === "All"
                    ? monthFiltered
                    : monthFiltered.filter(
                        (expense) => expense.ReimbursmentTrackingProgram === selectedProgram
                    );
            setExpenseData(programFiltered);
        } catch (error) {
            console.error("Error fetching expenses:", error);
        }
    };

    // Get unique programs from the expense data (ReimbursmentTrackingProgram)
    const reimbursementPrograms = Array.from(
        new Set(expenseData.map((expense) => expense.ReimbursmentTrackingProgram).filter(Boolean))
    ).sort();

    // Calculate totals for Amount and PO Amount
    const totalAmount = expenseData.reduce((acc, expense) => acc + expense.TotalAmount, 0);
    const totalPOAmount = expenseData.reduce((acc, expense) => acc + (parseFloat(expense.POAmount) || 0), 0);

    // Group expenses by VendorName
    const groupedData = expenseData.reduce((acc, expense) => {
        const vendor = expense.VendorName || "N/A";
        if (!acc[vendor]) {
            acc[vendor] = [];
        }
        acc[vendor].push(expense);
        return acc;
    }, {});
    const vendorKeys = Object.keys(groupedData).sort();

    const toggleGroup = (vendor) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [vendor]: !prev[vendor],
        }));
    };

    // Updated Excel export that mimics the grouped table view
    const handleDownloadExcel = () => {
        const exportRows = [];
        // Header row
        exportRows.push([
            "Expense Date",
            "Vendor Name",
            "Invoice Number",
            "Expense Category",
            "Expense Subcategory",
            "Description",
            "Amount",
            "Process ID",
            "District Name",
            "Site Name",
            "PO Number",
            "PO Amount",
            "PO Type",
            "Invoice Status",
            "Reimbursement Tracking ID",
            "Reimbursement Program",
            "Image",
        ]);

        // Group rows by vendor with header and expense rows
        vendorKeys.forEach((vendor) => {
            // Vendor header row (leave most cells blank, display vendor name and count)
            exportRows.push([
                "",
                `${vendor} (${groupedData[vendor].length})`,
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                ""
            ]);
            groupedData[vendor].forEach((exp) => {
                exportRows.push([
                    exp.ExpenseDate ? exp.ExpenseDate.toLocaleDateString() : "",
                    exp.VendorName || "",
                    exp.InvoiceNumber || "",
                    exp.ExpenseCategory || "",
                    exp.ExpenseSubcategory || "",
                    exp.Description || "",
                    exp.TotalAmount,
                    exp.ProcessID || "",
                    exp.DistrictName || "",
                    exp.SiteName || "",
                    exp.PONumber || "",
                    exp.POAmount,
                    exp.POType || "",
                    exp.InvoiceStatus || "",
                    exp.ReimbursementTrackingId || "",
                    exp.ReimbursmentTrackingProgram || "",
                    exp.imageURL || ""
                ]);
            });
        });

        // Optionally, add a totals row at the bottom
        exportRows.push([
            "Totals",
            "",
            "",
            "",
            "",
            "",
            totalAmount,
            "",
            "",
            "",
            "",
            totalPOAmount,
            "",
            "",
            "",
            "",
            ""
        ]);

        const sheet = XLSX.utils.aoa_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, "ReimbursementReport");
        XLSX.writeFile(workbook, "FederalReimbursementReport.xlsx");
    };

    let overallRowCounter = 0;

    return (
        <Box
            sx={{
                backgroundColor,
                color: textColor,
                padding: "20px",
                margin: "20px auto",
                borderRadius: "10px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                fontFamily: "Arial, sans-serif",
                lineHeight: 1.5,
            }}
        >
            {/* Download Button (Excel Only) */}
            <Box sx={{ display: "flex", gap: "10px", justifyContent: "center", mb: 2 }}>
                <Button variant="contained" onClick={handleDownloadExcel}>
                    Download as Excel
                </Button>
            </Box>

            {/* Report Container */}
            <Box sx={{ padding: "20px" }}>
                {/* Logo */}
                <Box
                    component="img"
                    src={figLogo}
                    alt="FIG Logo"
                    sx={{ display: "block", margin: "0 auto 20px", maxWidth: "150px" }}
                />

                {/* Header */}
                <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", textAlign: "center", mb: "20px", color: textColor }}
                >
                    Federal Reimbursement Report
                </Typography>

                {/* Filters */}
                <Box display="flex" justifyContent="center" gap="20px" mb="20px">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: textColor }}>Month</InputLabel>
                        <Select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            sx={{ backgroundColor, color: textColor }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            {Array.from({ length: 12 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: textColor }}>Program</InputLabel>
                        <Select
                            value={selectedProgram}
                            onChange={(e) => setSelectedProgram(e.target.value)}
                            sx={{ backgroundColor, color: textColor }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            {reimbursementPrograms.map((program) => (
                                <MenuItem key={program} value={program}>
                                    {program}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Report Table */}
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Expense Date
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Vendor Name
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Invoice Number
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Expense Category
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Expense Subcategory
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Description
                                </TableCell>
                                <TableCell align="right" sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Amount
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Process ID
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    District Name
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Site Name
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    PO Number
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    PO Amount
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    PO Type
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Invoice Status
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Reimbursement Tracking ID
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Reimbursement Program
                                </TableCell>
                                <TableCell sx={{ backgroundColor: headerBackground, fontWeight: "bold", color: textColor }}>
                                    Image
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vendorKeys.map((vendor) => (
                                <React.Fragment key={vendor}>
                                    <TableRow
                                        sx={{
                                            backgroundColor: headerBackground,
                                            cursor: "pointer",
                                        }}
                                        onClick={() => toggleGroup(vendor)}
                                    >
                                        <TableCell colSpan={17} sx={{ fontWeight: "bold", color: textColor }}>
                                            <IconButton size="small" sx={{ color: textColor }}>
                                                {collapsedGroups[vendor] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                            </IconButton>
                                            {vendor} ({groupedData[vendor].length})
                                        </TableCell>
                                    </TableRow>
                                    {!collapsedGroups[vendor] &&
                                        groupedData[vendor].map((exp) => {
                                            overallRowCounter++;
                                            const rowBgColor =
                                                overallRowCounter % 2 === 0 ? alternateRowEven : alternateRowOdd;
                                            return (
                                                <TableRow key={exp.ExpenseID} sx={{ backgroundColor: rowBgColor }}>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ExpenseDate ? exp.ExpenseDate.toLocaleDateString() : "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.VendorName || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.InvoiceNumber || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ExpenseCategory || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ExpenseSubcategory || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.Description || "N/A"}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: textColor }}>
                                                        {formatCurrency(exp.TotalAmount)}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ProcessID || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.DistrictName || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.SiteName || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.PONumber || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {formatCurrency(exp.POAmount)}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.POType || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.InvoiceStatus || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ReimbursementTrackingId || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.ReimbursmentTrackingProgram || "N/A"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: textColor }}>
                                                        {exp.imageURL ? (
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => {
                                                                    setMediaImageUrl(exp.imageURL);
                                                                    setMediaViewerOpen(true);
                                                                }}
                                                                style={{ backgroundColor: accentColor, color: "#000" }}
                                                            >
                                                                View Image
                                                            </Button>
                                                        ) : (
                                                            "No Image"
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </React.Fragment>
                            ))}
                            <TableRow
                                sx={{
                                    backgroundColor: alternateRowEven,
                                    "& .MuiTableCell-root": { color: textColor, fontWeight: "bold" },
                                }}
                            >
                                <TableCell colSpan={6}>Totals</TableCell>
                                <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell align="right">{formatCurrency(totalPOAmount)}</TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Media Viewer Modal for Invoice Image */}
            {mediaViewerOpen && (
                <MediaViewer
                    open={mediaViewerOpen}
                    src={mediaImageUrl}
                    onClose={() => setMediaViewerOpen(false)}
                    type="image"
                />
            )}
        </Box>
    );
};

export default FederalReimbursementReport;
